import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Award, Layers, Users } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PageHeader, Card } from "@/components/ranger/PageHeader";
import { ConfigForm, CrudTable, itemsFmt } from "@/components/ranger/Crud";
import { ConfirmButton } from "@/components/ranger/ConfirmButton";
import { channelOptions } from "@/pages/Feeds";
import { BTN_PRIMARY, BTN_SECONDARY } from "@/pages/Servers";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const configFields = (chans) => [
  { key: "enabled", label: "Battle pass enabled", type: "switch" },
  { key: "ingame_levelup", label: "Announce level-ups in game (say)", type: "switch" },
  { key: "xp_per_kill", label: "XP per PvP kill", type: "number" },
  { key: "xp_per_hour", label: "XP per hour online", type: "number" },
  { key: "xp_per_daily", label: "XP per daily claim", type: "number" },
  { key: "feed_channel_id", label: "Level-up Discord channel", type: "select", options: channelOptions(chans) },
  { key: "daily_challenges", label: "Daily challenges (kills, playtime, /daily)", type: "switch" },
  { key: "season", label: "Season name", hint: "Premium is granted by staff per season. When you start a new season, change this and use Wipe progress so premium rewards aren't paid twice." },
];
const levelFields = [
  { key: "level", label: "Level", type: "number", default: 1 },
  { key: "title", label: "Title (optional)", default: "" },
  { key: "xp_required", label: "Total XP required", type: "number", default: 100 },
  { key: "reward_coins", label: "Coin reward", type: "number", default: 0 },
  { key: "items", label: "Item rewards (optional, one per line)", type: "items", hint: "Delivered like shop orders (auto-deliver must be on, else staff deliver)." },
  { key: "premium_coins", label: "Premium track coins", type: "number", default: 0 },
  { key: "premium_items", label: "Premium track items (one per line)", type: "items", hint: "Only premium players get these." },
];
const levelCols = [["level", "Level"], ["title", "Title"], ["xp_required", "XP"], ["reward_coins", "Coins"], ["items", "Items", itemsFmt], ["premium_coins", "Premium coins"], ["premium_items", "Premium items", itemsFmt]];

const GrantPremiumGate = ({ cid }) => {
  const { can } = useAuth();
  return can("admin") ? <GrantPremium cid={cid} /> : null;
};

const GrantPremium = ({ cid }) => {
  const [tag, setTag] = useState("");
  const go = async (e) => { e.preventDefault(); try { const { data } = await api.post(`/communities/${cid}/battlepass/premium`, { gamertag: tag }); toast.success(data.granted ? `${data.gamertag} now has premium` : `${data.gamertag} already has premium`); setTag(""); } catch (err) { toast.error(errMsg(err)); } };
  return (
    <Card title="Grant premium" icon={Award} testId="bp-grant-premium-card" className="mb-5">
      <form onSubmit={go} className="flex flex-wrap gap-2"><Input data-testid="bp-premium-gamertag" placeholder="Linked gamertag" value={tag} onChange={(e) => setTag(e.target.value)} className={`w-64 ${inputCls}`} /><button data-testid="bp-premium-grant" disabled={!tag.trim()} className={BTN_PRIMARY}>Grant for this season</button></form>
      <p className="mt-2 text-xs text-slate-500">For giveaways, events or rewarding helpful players.</p>
    </Card>
  );
};
const inputCls = "border-[#1E2D4A] bg-[#050811] font-mono";

const Progress = ({ cid, admin }) => {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ discord_id: "", xp: "" });
  const load = useCallback(() => api.get(`/communities/${cid}/battlepass/progress`).then((r) => setRows(r.data)).catch(() => {}), [cid]);
  useEffect(() => { load(); }, [load]);
  const give = async (e) => { e.preventDefault(); try { const { data } = await api.post(`/communities/${cid}/battlepass/give-xp`, { discord_id: f.discord_id, xp: Number(f.xp) }); toast.success(`Now level ${data.level} (${data.xp} XP)`); setF({ discord_id: "", xp: "" }); load(); } catch (err) { toast.error(errMsg(err)); } };
  const wipe = async () => { try { const { data } = await api.post(`/communities/${cid}/battlepass/wipe`); toast.success(`Reset ${data.players} players`); load(); } catch (err) { toast.error(errMsg(err)); } };
  return (
    <Card title="Player progress" icon={Users} testId="bp-progress-card"
      right={admin && <ConfirmButton testId="bp-wipe-button" className={BTN_SECONDARY} title="Wipe the battle pass?" description="Every player's XP and level resets to 0. Rewards already given are kept." onConfirm={wipe}>Wipe season</ConfirmButton>}>
      {admin && <form onSubmit={give} className="mb-4 grid gap-2 sm:grid-cols-[1.5fr_1fr_auto]">
        <Input data-testid="bp-give-discord-id" required placeholder="Discord ID" value={f.discord_id} onChange={(e) => setF({ ...f, discord_id: e.target.value })} className={inputCls} />
        <Input data-testid="bp-give-xp" required type="number" min={1} placeholder="XP" value={f.xp} onChange={(e) => setF({ ...f, xp: e.target.value })} className={inputCls} />
        <button data-testid="bp-give-submit" className={BTN_PRIMARY}>Give XP</button>
      </form>}
      {rows.length === 0 ? <p className="text-sm text-slate-500">No XP earned yet. Linked players earn XP from kills, playtime and daily claims.</p> : (
        <ul data-testid="bp-progress-list" className="divide-y divide-[#1E2D4A]">{rows.map((r) => (
          <li key={r.discord_id} className="flex justify-between py-2 text-sm"><button onClick={() => setF({ ...f, discord_id: r.discord_id })} className="text-left text-slate-200 hover:text-white">{r.gamertag || "Unlinked"} <span className="font-mono text-xs text-slate-500">{r.discord_id}</span></button><span className="font-mono text-slate-300">L{r.level} · {r.xp} XP</span></li>
        ))}</ul>
      )}
    </Card>
  );
};

const StarterPack = ({ cid, admin, onDone }) => {
  if (!admin) return null;
  const add = async () => { try { const { data } = await api.post(`/communities/${cid}/battlepass/starter-pack`); toast.success(data.added ? `Added ${data.added} levels` : "All 20 starter levels already exist"); onDone?.(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-sm border border-dashed border-[#E5202E]/40 px-4 py-3">
      <p className="text-sm text-slate-300">Season Starter Pack: 20 levels from wood and stone up to an AK, with coins every level. Fully editable after.</p>
      <button data-testid="bp-starter-pack" onClick={add} className={BTN_PRIMARY}>Add starter season</button>
    </div>
  );
};

export default function BattlePass() {
  const { active, can } = useAuth();
  const [chans, setChans] = useState(null);
  const [packKey, setPackKey] = useState(0);
  useEffect(() => { if (can("admin")) api.get(`/communities/${active.id}/discord/channels`).then((r) => setChans(r.data)).catch(() => setChans([])); else setChans([]); }, [active.id, can]);
  return (
    <div>
      <PageHeader eyebrow="Progression" title="Battle Pass" description="Linked players earn XP from PvP kills, time online and daily claims. Each level pays out once." />
      {chans && <ConfigForm key={chans.length} cid={active.id} field="battlepass" fields={configFields(chans)} title="Battle pass settings" icon={Award} />}
      <GrantPremiumGate cid={active.id} />
      <StarterPack cid={active.id} admin={can("admin")} onDone={() => setPackKey((k) => k + 1)} />
      <CrudTable key={packKey} cid={active.id} path="bp-levels" title="Levels" icon={Layers} fields={levelFields} columns={levelCols} emptyText="Add levels with the XP needed and the reward for reaching them." />
      <Progress cid={active.id} admin={can("admin")} />
    </div>
  );
}
